import { useFrame } from "@react-three/fiber"
import { useRef } from "react"
import type { Group, Mesh } from "three"
import type { LandingData } from "../data/types"
import { scenePresence, sectionFocus, smoothstep, type LandingMotionRef } from "../lib/landing-motion"
import { applyGroupOpacity } from "../lib/three"
import { signalColor } from "../lib/signal"
import { SceneText } from "./SceneText"

export function SignalLattice({ data, motion }: { data: LandingData; motion: LandingMotionRef }) {
  const group = useRef<Group>(null)
  const tileRefs = useRef<Array<Group | null>>([])
  const scanRef = useRef<Mesh>(null)

  useFrame(() => {
    const section = motion.current.section
    const presence = scenePresence(section, 1.2, 3.45, 0.46)
    const focus = sectionFocus(section, 2, 0.9)
    if (!group.current) return
    group.current.position.set(-2.32 + focus * 0.16, -0.1, -0.72 + presence * 0.82)
    group.current.rotation.x = -0.22 + focus * 0.08
    group.current.rotation.y = 0.08 + (section - 2) * 0.08
    group.current.scale.setScalar(1.54 + focus * 0.12)
    applyGroupOpacity(group.current, presence)

    const sweep = smoothstep(1.35, 2.85, section)
    if (scanRef.current) {
      scanRef.current.position.x = -3.25 + sweep * 6.5
      scanRef.current.scale.y = 0.8 + focus * 0.42
    }

    tileRefs.current.forEach((tile, i) => {
      if (!tile) return
      const sig = data.lattice[i]?.sig
      const emphasis = sig === "LEADER" || sig === "STRESS" ? 0.1 : 0
      const wave = Math.sin(section * 3.2 + i * 0.18) * 0.035
      tile.position.z = wave * presence
      tile.scale.setScalar(1 + focus * emphasis)
    })
  })

  return (
    <group ref={group} scale={1.62}>
      {data.lattice.map((tile, i) => {
        const col = i % 12
        const row = Math.floor(i / 12)
        const color = signalColor(tile.sig)
        const x = (col - 5.5) * 0.52
        const y = (3.5 - row) * 0.36
        return (
          <group
            key={`${tile.region}-${tile.kind}-${tile.sector}`}
            position={[x, y, 0]}
            ref={(el) => {
              tileRefs.current[i] = el
            }}
          >
            <mesh>
              <boxGeometry args={[0.42, 0.055, 0.3]} />
              <meshBasicMaterial color={color} transparent opacity={tile.symbol === "-" ? 0.08 : 0.58} />
            </mesh>
            {tile.symbol !== "-" && i % 4 === 0 ? (
              <SceneText
                position={[0, 0.19, 0.03]}
                fontSize={0.13}
                color="#f2f5f4"
                fillOpacity={0.42}
                outlineWidth={0.006}
                outlineColor="#020404"
              >
                {tile.symbol}
              </SceneText>
            ) : null}
          </group>
        )
      })}
      <mesh ref={scanRef} position={[-3.25, 0, 0.09]}>
        <boxGeometry args={[0.035, 3.2, 0.08]} />
        <meshBasicMaterial color="#7af0d8" transparent opacity={0.58} />
      </mesh>
    </group>
  )
}
